import React, {useState} from 'react';
import {
    Card,
    Row,
    Col,
    Button,
    Table,
    Alert,
    ProgressBar
} from 'react-bootstrap';
import {useHistory} from 'react-router-dom';
import {toast} from 'react-toastify';
import {Post, Get} from '@app/utils/httpProvider';
import {ASSET_API, FILE_MANAGER_API} from '@app/utils/apiUrl';
import {URI_INTERNAL} from '@app/utils/pathLocations';
import ReactSpinner from '@app/components/reactSpinner/ReactSpinner';
import {fetchWithAuth} from '@app/utils/httpClient';
import {getToken} from '@app/utils/helper';
import axios from 'axios';

const AssetBulkImport = () => {
    const history = useHistory();
    const [file, setFile] = useState(null);
    const [mediaFiles, setMediaFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const handleMediaFilesChange = (e) => {
        if (e.target.files) {
            setMediaFiles(Array.from(e.target.files));
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
            setResult(null);
            setError(null);
        }
    };

    const downloadTemplate = async () => {
        console.log('Bắt đầu tải file mẫu...');
        try {
            const response = await fetchWithAuth(ASSET_API.DOWNLOAD_TEMPLATE, {
                method: 'GET'
            });

            if (response.ok) {
                const blob = await response.blob();
                const fileName = 'Template_Asset_Import.xlsx';

                // Giải pháp an toàn nhất cho Blob download
                const url = window.URL.createObjectURL(
                    new Blob([blob], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    })
                );
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();

                console.log('Đã kích hoạt tải xuống: ' + fileName);

                setTimeout(() => {
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                }, 200);
            } else {
                toast.error(
                    'Không thể tải file mẫu. Lỗi xác thực hoặc máy chủ.'
                );
            }
        } catch (err) {
            console.error('Lỗi download:', err);
            toast.error('Có lỗi xảy ra khi tải file mẫu.');
        }
    };

    const handleUpload = async () => {
        if (!file) {
            toast.warn('Vui lòng chọn file trước.');
            return;
        }

        setIsUploading(true);
        setResult(null);
        setError(null);

        try {
            // Bước 1: Upload toàn bộ mediaFiles (nếu có) để tạo mapping
            const mediaMap = {};
            if (mediaFiles.length > 0) {
                const token = await getToken();
                const config = {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        Authorization: `bearer ${token}`
                    }
                };

                for (const mediaFile of mediaFiles) {
                    const data = new FormData();
                    data.append('file', mediaFile);
                    try {
                        const uploadRes = await axios.post(FILE_MANAGER_API.UPLOAD, data, config);
                        const fileUpdated = uploadRes.data ? uploadRes.data[0] : null;
                        if (fileUpdated) {
                            // Map tên gốc -> relative path
                            mediaMap[mediaFile.name] = fileUpdated.filePath;
                        }
                    } catch (uploadErr) {
                        console.error(`Lỗi khi tải lên file ${mediaFile.name}:`, uploadErr);
                        toast.error(`Lỗi khi tải lên file ${mediaFile.name}.`);
                        setIsUploading(false);
                        return;
                    }
                }
            }

            // Bước 2: Upload Excel cùng với mediaMapJson
            const formData = new FormData();
            formData.append('file', file);
            formData.append('mediaMapJson', JSON.stringify(mediaMap));

            // Sử dụng fetchWithAuth để tự động đính kèm Token xác thực vào Header
            const response = await fetchWithAuth(ASSET_API.BULK_IMPORT, {
                method: 'POST',
                body: formData // Gửi dữ liệu dưới dạng Multipart thay vì JSON
            });

            if (response.ok) {
                const data = await response.json(); // Giải mã kết quả trả về từ server (JSON)
                setResult(data); // Lưu kết quả vào state để hiển thị danh sách lỗi/thành công
                if (data.totalRows === 0) {
                    toast.warn('Không có dữ liệu để import.');
                } else {
                    if (data.successCount > 0) {
                        toast.success(
                            `Nhập thành công ${data.successCount} tài sản.`
                        );
                    }
                    if (data.errorCount > 0) {
                        toast.error(`Có ${data.errorCount} dòng bị lỗi.`);
                    }
                }
            } else {
                // Xử lý khi server trả về mã lỗi (400, 401, 500, v.v.)
                let errorMessage = 'Có lỗi xảy ra khi upload.';
                try {
                    const errData = await response.json();
                    errorMessage = errData.message || errorMessage;
                } catch (e) {
                    console.error('Không thể đọc phản hồi lỗi:', e);
                }
                setError(errorMessage);
            }
        } catch (err) {
            console.error('Lỗi upload:', err);
            setError('Lỗi kết nối máy chủ.');
        } finally {
            setIsUploading(false); // Kết thúc trạng thái đang xử lý
        }
    };

    return (
        <div className="container-fluid">
            {isUploading && <ReactSpinner />}

            <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white py-3 d-flex align-items-center">
                    <h5 className="mb-0 fw-bold text-primary">
                        <i className="fas fa-file-import mr-2" /> Nhập tài sản từ Excel
                    </h5>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="ml-auto d-inline-flex align-items-center justify-content-center"
                        onClick={() => history.push(URI_INTERNAL.ASSET_PATH)}
                        title="Quay lại danh sách"
                    >
                        <i className="fas fa-arrow-left" />
                        <span className="d-none d-sm-inline ml-1">
                            Quay lại danh sách
                        </span>
                    </Button>
                </Card.Header>
                <Card.Body className="p-4">
                    <Row className="justify-content-center">
                        <Col md={8}>
                            <div
                                className="bg-light p-4 rounded text-center border-dashed mb-4"
                                style={{border: '2px dashed #ddd'}}
                            >
                                <i className="fas fa-cloud-upload-alt fa-3x text-muted mb-3" />
                                <h6 className="fw-bold">
                                    Chọn file Excel (.xlsx)
                                </h6>
                                <p className="text-muted small">
                                    Vui lòng sử dụng file mẫu để đảm bảo dữ liệu
                                    chính xác.
                                </p>

                                <div className="d-flex justify-content-center gap-2 mt-3">
                                    <input
                                        type="file"
                                        id="bulk-file"
                                        className="d-none"
                                        accept=".xlsx"
                                        onChange={handleFileChange}
                                    />
                                    <Button
                                        variant="primary"
                                        className="mx-2"
                                        onClick={() =>
                                            document
                                                .getElementById('bulk-file')
                                                .click()
                                        }
                                    >
                                        {file
                                            ? 'Thay đổi file'
                                            : 'Chọn file từ máy tính'}
                                    </Button>
                                    <Button
                                        variant="outline-info"
                                        className="mx-2"
                                        onClick={downloadTemplate}
                                    >
                                        <i className="fas fa-download mr-1" />{' '}
                                        Tải file mẫu
                                    </Button>
                                </div>

                                {file && (
                                    <div className="mt-3 p-2 bg-white border rounded d-inline-block">
                                        <i className="fas fa-file-excel text-success mr-2" />
                                        <strong>{file.name}</strong> (
                                        {Math.round(file.size / 1024)} KB)
                                    </div>
                                )}
                            </div>

                            <div
                                className="bg-light p-4 rounded text-center border-dashed mb-4"
                                style={{border: '2px dashed #ddd'}}
                            >
                                <i className="fas fa-images fa-3x text-muted mb-3" />
                                <h6 className="fw-bold">
                                    Chọn Tệp đính kèm (Ảnh/Tài liệu)
                                </h6>
                                <p className="text-muted small">
                                    Chọn tất cả ảnh hoặc tài liệu có tên khớp với tên file bạn nhập trong Excel (Cột S và T).
                                </p>

                                <div className="d-flex justify-content-center gap-2 mt-3">
                                    <input
                                        type="file"
                                        id="bulk-media"
                                        className="d-none"
                                        multiple
                                        accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        onChange={handleMediaFilesChange}
                                    />
                                    <Button
                                        variant="outline-primary"
                                        onClick={() =>
                                            document
                                                .getElementById('bulk-media')
                                                .click()
                                        }
                                    >
                                        {mediaFiles.length > 0
                                            ? `Thay đổi tệp (${mediaFiles.length} tệp đã chọn)`
                                            : 'Chọn tệp đính kèm'}
                                    </Button>
                                </div>

                                {mediaFiles.length > 0 && (
                                    <div className="mt-3 text-left" style={{maxHeight: '150px', overflowY: 'auto'}}>
                                        <ul className="list-group list-group-flush small">
                                            {mediaFiles.map((f, i) => (
                                                <li key={i} className="list-group-item bg-transparent py-1">
                                                    <i className="fas fa-paperclip mr-2 text-info" />
                                                    {f.name} ({Math.round(f.size / 1024)} KB)
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>

                            <div className="text-center">
                                <Button
                                    variant="success"
                                    size="lg"
                                    className="px-5 shadow-sm"
                                    disabled={!file || isUploading}
                                    onClick={handleUpload}
                                >
                                    {isUploading
                                        ? 'Đang xử lý...'
                                        : 'Bắt đầu Import dữ liệu'}
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {error && (
                        <Alert variant="danger" className="mt-4">
                            <i className="fas fa-exclamation-triangle mr-2" />{' '}
                            {error}
                        </Alert>
                    )}

                    {result && (
                        <div className="mt-5">
                            <h6 className="fw-bold mb-3 border-bottom pb-2">
                                Kết quả nhập dữ liệu:
                            </h6>
                            <Row className="mb-4">
                                <Col md={4}>
                                    <Card className="text-center border-0 bg-light shadow-none">
                                        <Card.Body>
                                            <div className="text-muted small uppercase">
                                                Tổng số dòng
                                            </div>
                                            <div className="h3 fw-bold">
                                                {result.totalRows}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={4}>
                                    <Card className="text-center border-0 bg-success-light shadow-none">
                                        <Card.Body>
                                            <div className="text-success small uppercase">
                                                Thành công
                                            </div>
                                            <div className="h3 fw-bold text-success">
                                                {result.successCount}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={4}>
                                    <Card className="text-center border-0 bg-danger-light shadow-none">
                                        <Card.Body>
                                            <div className="text-danger small uppercase">
                                                Thất bại
                                            </div>
                                            <div className="h3 fw-bold text-danger">
                                                {result.errorCount}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            {result.errors && result.errors.length > 0 && (
                                <>
                                    <h6 className="text-danger fw-bold mb-2">
                                        Chi tiết các dòng lỗi:
                                    </h6>
                                    <div className="table-responsive shadow-sm rounded">
                                        <Table hover bordered className="mb-0">
                                            <thead className="bg-danger text-white">
                                                <tr>
                                                    <th
                                                        width="80"
                                                        className="text-center"
                                                    >
                                                        Dòng
                                                    </th>
                                                    <th width="150">Mã TS</th>
                                                    <th>Nội dung lỗi</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {result.errors.map(
                                                    (err, idx) => (
                                                        <tr key={idx}>
                                                            <td className="text-center fw-bold">
                                                                {err.rowIndex}
                                                            </td>
                                                            <td className="text-secondary">
                                                                {err.assetCode ||
                                                                    '—'}
                                                            </td>
                                                            <td className="text-danger">
                                                                {
                                                                    err.errorMessage
                                                                }
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </>
                            )}

                            {result.errorCount === 0 &&
                                result.successCount > 0 && (
                                    <Alert
                                        variant="success"
                                        className="text-center"
                                    >
                                        <i className="fas fa-check-circle mr-2" />{' '}
                                        Tất cả dữ liệu đã được nhập thành công
                                        vào hệ thống.
                                    </Alert>
                                )}

                            {result.totalRows === 0 && (
                                <Alert
                                    variant="warning"
                                    className="text-center"
                                >
                                    <i className="fas fa-exclamation-triangle mr-2" />{' '}
                                    Không có dữ liệu để import.
                                </Alert>
                            )}
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Alert variant="info" className="border-0 shadow-sm">
                <h6 className="fw-bold">
                    <i className="fas fa-info-circle mr-2" /> Hướng dẫn quan
                    trọng:
                </h6>
                <ul className="mb-0 small">
                    <li>
                        Các cột có đánh dấu <strong>(*)</strong> là bắt buộc
                        phải nhập.
                    </li>
                    <li>
                        <strong>Nhóm tài sản, Phòng ban, Trạng thái:</strong>{' '}
                        Phải khớp chính xác với tên đã khai báo trong danh mục
                        hệ thống (không phân biệt hoa thường).
                    </li>
                    <li>
                        <strong>Ngày mua:</strong> Định dạng chuẩn là
                        Ngày/Tháng/Năm (ví dụ: 15/05/2023).
                    </li>
                    <li>
                        <strong>Giá trị mua:</strong> Nhập số nguyên hoặc số
                        thập phân, không chứa dấu phân cách hàng nghìn.
                    </li>
                    <li>
                        <strong>Tải lên hình ảnh tài sản:</strong> Nếu muốn nhập kèm ảnh tài sản, bạn phải <strong>chọn cả file Excel lẫn các tệp hình ảnh cùng lúc</strong> trước khi bấm nút <strong>Import</strong>. Hệ thống không hỗ trợ tải riêng lẻ hình ảnh sau khi đã thực hiện import file Excel.
                        <br />
                        <em>Lưu ý:</em> Trong cột hình ảnh của file Excel, <strong>chỉ điền tên file ảnh kèm phần mở rộng</strong> (ví dụ: <code>maytinh.png</code>, <code>laptop.jpg</code>). <strong>Không copy toàn bộ đường dẫn thư mục trên máy tính của bạn</strong> (ví dụ: <code>C:\Users\Admin\Pictures\maytinh.png</code>) vì hệ thống sẽ không thể nhận diện được.
                    </li>
                </ul>
            </Alert>
        </div>
    );
};

export default AssetBulkImport;
